import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useNavigate, useParams } from 'react-router';

import { CheckinPageShell } from '@/components/checkin-page-shell';
import { ChurchBrandMark } from '@/components/church-brand-mark';
import { DisplayScreen } from '@/components/display-screen';
import { GuestFormScreen } from '@/components/guest-form-screen';
import { PoweredByFooter } from '@/components/powered-by-footer';
import { api } from '@/lib/api';
import { readGatewayAuth, writeGatewayAuth } from '@/lib/gateway-auth';

interface PairingStatus {
  kiosk: {
    id: string;
    churchId: string;
    name: string;
    status: string;
    pairingCode: string;
  };
  church: { name: string; campusName: string; logoUrl: string | null };
  accessToken: string | null;
}

const KioskIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6zm2 0v12h12V6H6zm3 14h6v2H9v-2z" />
  </svg>
);

const PairingScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pairingCode = '' } = useParams();
  const [status, setStatus] = useState<PairingStatus | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const existing = readGatewayAuth(pairingCode);
    if (existing) {
      void navigate(`/display/${encodeURIComponent(pairingCode)}`, { replace: true });
    }
  }, [navigate, pairingCode]);

  useEffect(() => {
    if (!pairingCode) return;
    const load = async () => {
      try {
        const data = await api.get<PairingStatus>(
          `/gateway/attendance/kiosks/${encodeURIComponent(pairingCode)}`,
        );
        setStatus(data);
        setError(false);

        if (data.kiosk.status === 'paired' && data.accessToken) {
          writeGatewayAuth({
            accessToken: data.accessToken,
            pairingCode,
            kioskId: data.kiosk.id,
            churchId: data.kiosk.churchId,
          });
          void navigate(`/display/${encodeURIComponent(pairingCode)}`, { replace: true });
        }
      } catch {
        setError(true);
      }
    };
    void load();
    const timer = setInterval(() => void load(), 5_000);
    return () => clearInterval(timer);
  }, [navigate, pairingCode]);

  const isPaired = status?.kiosk.status === 'paired';

  return (
    <CheckinPageShell footer={<PoweredByFooter />}>
      <section className="checkin-card checkin-card--centered">
        {status ? (
          <ChurchBrandMark
            churchName={status.church.name}
            campusName={status.church.campusName}
            logoUrl={status.church.logoUrl}
            title={t('pairing.title')}
            description={isPaired ? t('pairing.paired') : t('pairing.waiting')}
          />
        ) : (
          <header className="church-brand">
            <div className="church-brand-logo-fallback" aria-hidden>
              <KioskIcon />
            </div>
            <h1 className="church-brand-title">{t('pairing.title')}</h1>
          </header>
        )}

        <div className="checkin-pairing-code">
          <p className="checkin-pairing-code-label">{t('pairing.code')}</p>
          <p className="checkin-pairing-code-value">
            {status?.kiosk.pairingCode ?? pairingCode}
          </p>
        </div>

        {!isPaired ? (
          <p className="checkin-card-note">{t('pairing.instructions')}</p>
        ) : null}

        {error ? <p className="checkin-error">{t('error')}</p> : null}
      </section>
    </CheckinPageShell>
  );
};

const Home = () => {
  const { t } = useTranslation();

  return (
    <CheckinPageShell footer={<PoweredByFooter />}>
      <section className="checkin-card checkin-card--centered">
        <header className="church-brand">
          <div className="church-brand-logo-fallback" aria-hidden>
            <KioskIcon />
          </div>
          <h1 className="church-brand-title">{t('title')}</h1>
          <p className="church-brand-description">{t('home.lead')}</p>
        </header>
      </section>
    </CheckinPageShell>
  );
};

export const App = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/pair/:pairingCode" element={<PairingScreen />} />
    <Route path="/display/:pairingCode" element={<DisplayScreen />} />
    <Route path="/guest/:pairingCode" element={<GuestFormScreen />} />
  </Routes>
);
